// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The evidence sources behind a survey selection (Plan / 360° / Foto /
 * Punktsky / Rum), as data. Moved out of `components/kortlaegning/EvidencePanel`
 * so the Bygning shell's evidence tile (WP-F) and the Ressourcer inspector
 * (WP-D) share the rules without importing a retiring component. Builds URLs
 * through the API client but touches no DOM and no React.
 *
 * The Foto source is driven entirely by `view` (the resolved `sys:view`, from
 * `selectionView` in `kortlaegning/view.ts`) — there is deliberately no
 * fallback to a ranked "best frame": `view: null` (or omitted) shows
 * `VIEW_TEXT.none`, "Intet billede valgt". The 360° tab takes the resolved
 * panorama as an optional fourth argument (`resolvePano` in
 * `kortlaegning/pano.ts`); omitted = still loading.
 */

import { api } from '../api/client';
import type { InstancePanorama, ResourceView, SurveyPart, SurveyType } from '../api/types';
import { viewportPanoramaHref } from '../app/links';
import type { EvidenceTab } from './keys';
import { PANO_STRIP_MAX_SIZE, PANO_TEXT, panoCaption } from './pano';
import { hasInstanceLink, resolveHighlightPart } from './photo';
import { VIEW_TEXT, viewCaption, viewFetchSize, viewImageUrl } from './view';

export { resolveHighlightPart } from './photo';

export interface EvidenceSource {
  tab: EvidenceTab;
  label: string;
  caption: string;
  url: string | null;
  empty: string;
  /** Foto only: the chosen view, drawn cropped (ViewImage). */
  view?: ResourceView;
  /** 360° only: the panorama, and where the part is in it (u, v in 0..1). */
  pano?: { id: number; u: number; v: number; marker: boolean };
  /** 360° only: "Åbn i viewport" (`?pano=<id>`). */
  href?: string;
}

export const RENDER_SIZE = { width: 640, height: 480 };

/** Shown when an image exists but could not be drawn. */
export const GENERIC_IMAGE_ERROR =
  'Billedet kunne ikke tegnes — serveren mangler måske 3D-visning, eller projektet mangler data til denne visning.';

export const EVIDENCE_LABEL: Record<EvidenceTab, string> = {
  plan: 'Plan',
  pano: '360°',
  foto: 'Foto',
  punktsky: 'Punktsky',
  rum: 'Rum',
};

/**
 * The five evidence sources for a selection, in `EVIDENCE_TABS` order. See
 * the module doc for `view` / `pano`.
 */
export function evidenceSources(
  type: SurveyType | null,
  part: SurveyPart | null,
  view?: ResourceView | null,
  pano?: { pano: InstancePanorama | null; empty: string },
): EvidenceSource[] {
  const highlight = resolveHighlightPart(type, part);
  const linked = hasInstanceLink(highlight);
  const highlightQuery = linked ? { highlight_instance: highlight.instance_id, highlight_cloud: highlight.cloud } : {};

  const fotoSource: EvidenceSource = view
    ? {
        tab: 'foto',
        label: EVIDENCE_LABEL.foto,
        caption: viewCaption(view),
        url: viewImageUrl(view, viewFetchSize(view, 960)),
        empty: GENERIC_IMAGE_ERROR,
        view,
      }
    : { tab: 'foto', label: EVIDENCE_LABEL.foto, caption: 'Ressourcebillede', url: null, empty: VIEW_TEXT.none };

  const panoSource: EvidenceSource = {
    tab: 'pano',
    label: EVIDENCE_LABEL.pano,
    caption: panoCaption(highlight?.room_name),
    url: null,
    empty: linked ? (pano?.empty ?? PANO_TEXT.loading) : PANO_TEXT.unlinked,
  };
  if (linked && pano?.pano) {
    const p = pano.pano;
    panoSource.url = api.panoramaImageUrl(p.panorama_id, { maxSize: PANO_STRIP_MAX_SIZE });
    panoSource.caption = panoCaption(highlight.room_name, p.heading);
    panoSource.empty = GENERIC_IMAGE_ERROR;
    panoSource.pano = { id: p.panorama_id, u: p.u, v: p.v, marker: p.heading === 'resected' };
    panoSource.href = viewportPanoramaHref(p.panorama_id);
  }

  return [
    {
      tab: 'plan',
      label: EVIDENCE_LABEL.plan,
      caption: 'Stueplan · snit i 1,2 m',
      url: api.renderUrl({ view: 'plan', layers: ['cloud'], ...RENDER_SIZE, ...highlightQuery }),
      empty: GENERIC_IMAGE_ERROR,
    },
    panoSource,
    fotoSource,
    {
      tab: 'punktsky',
      label: EVIDENCE_LABEL.punktsky,
      caption: 'Punktsky · bygningsdel markeret',
      url: api.renderUrl({
        view: 'orbit',
        orbit_index: 1,
        layers: ['cloud'],
        ...RENDER_SIZE,
        ...highlightQuery,
      }),
      empty: GENERIC_IMAGE_ERROR,
    },
    {
      tab: 'rum',
      label: EVIDENCE_LABEL.rum,
      caption: 'Rumvis model · segmenterede rum',
      url: api.renderUrl({ view: 'orbit', orbit_index: 1, layers: ['rooms'], ...RENDER_SIZE }),
      empty: GENERIC_IMAGE_ERROR,
    },
  ];
}
